/*
===================================================================================================
  This service will handle printer api request for LASER
===================================================================================================
*/

import { PrinterType } from '../../enums/enum';
import PrinterData from '../../@types/PrinterData.type';
import createNewApiClient from '../apiClient';
import { getResponseArray } from '../../utils/responseArray';

const getLaserPrintersRestPath = '/jetstream/api/1/getlaserprinters';

const normalizePrinterResponse = (responseData: unknown): PrinterData[] => {
  const printerNodes = getResponseArray(responseData, 'printers');

  return printerNodes
    .map((printer) => {
      if (!printer || typeof printer !== 'object') {
        return undefined;
      }

      const printerRecord = printer as Record<string, unknown>;

      const code =
        printerRecord.code ?? printerRecord.Code ?? printerRecord.value;
      const networkPath =
        printerRecord.networkPath ??
        printerRecord.NetworkPath ??
        printerRecord.path;

      if (typeof code !== 'string' || typeof networkPath !== 'string') {
        return undefined;
      }

      return {
        code: code.trim(),
        networkPath: networkPath.trim(),
      };
    })
    .filter((printer): printer is PrinterData => Boolean(printer));
};

const printerApiRequestService = async (
  url: string,
  station: string,
  printerType: string,
  accessToken?: string,
): Promise<PrinterData[]> => {
  // Throws error if url is not passed in the function as parameter
  if (!url) {
    throw new Error('URL is not provided');
  }

  // Throws error if station is not passed in the function as parameter
  if (!station) {
    throw new Error('Station is not provided');
  }

  if (printerType !== PrinterType.LASER) {
    throw new Error('Invalid printer type');
  }

  if (url !== getLaserPrintersRestPath) {
    throw new Error('Invalid endpoint for printer API');
  }

  if (!accessToken?.trim()) {
    throw new Error('Access token is not provided');
  }

  const response = await createNewApiClient(accessToken).get(
    getLaserPrintersRestPath,
    {
      params: {
        station: station.toString()?.trim(),
      },
    },
  );

  return normalizePrinterResponse(response?.data);
};

export default printerApiRequestService;
